import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import { useConfigList } from '../hooks/useConfigList';
import { useConfigCategories } from '../hooks/useConfigCategories';
import { useCreateConfig, useUpdateConfig } from '../hooks/useSaveConfig';
import { useDeleteConfig } from '../hooks/useDeleteConfig';
import { ConfigEntryFormDialog } from '../components/ConfigEntryFormDialog';
import { ConfigChangeFeedDrawer } from '../components/ConfigChangeFeedDrawer';
import { ConfigCommonUsagePanel } from '../components/ConfigCommonUsagePanel';
import type { ConfigEntryResponse, ApiError } from '../types/configCenter';
import type { ConfigEntryFormValues } from '../schema/configCenter.schema';

const DEFAULT_PAGE_SIZE = 20;

// 보호 키 세트 — spec §5.5 기준 (BE 와 동기화)
const PROTECTED_KEYS = new Set([
  'system.maintenance-mode',
  'system.master-key-id',
]);

/** Skeleton 행 */
function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i * 13) % 40}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/**
 * ConfigCenterListPage — 설정 센터 메인 페이지 (/config-center)
 *
 * 5상태:
 * - loading: DataTable skeleton 5행
 * - empty: EmptyState + [설정 추가]
 * - error: ErrorState (403 = permission hint / others = retry)
 * - default: DataTable 6컬럼 (키/값/타입/민감/수정자/작업)
 * - dialog: ConfigEntryFormDialog (생성/수정) · confirm dialog (삭제) · ConfigChangeFeedDrawer
 */
export function ConfigCenterListPage() {
  const { t, i18n } = useTranslation();

  // 필터 상태 (편집 중)
  const [categoryInput, setCategoryInput] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const [sensitiveOnlyInput, setSensitiveOnlyInput] = useState(false);

  // 적용된 필터 (검색 버튼 클릭 시 반영)
  const [appliedFilters, setAppliedFilters] = useState({
    category: '',
    keyword: '',
    sensitiveOnly: false,
    page: 0,
  });

  // dialog/drawer 상태
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ConfigEntryResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConfigEntryResponse | null>(null);
  const [changesOpen, setChangesOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  // 쿼리
  const listQuery = useConfigList({
    category: appliedFilters.category || undefined,
    keyword: appliedFilters.keyword || undefined,
    sensitiveOnly: appliedFilters.sensitiveOnly || undefined,
    page: appliedFilters.page,
    size: DEFAULT_PAGE_SIZE,
    sort: 'configKey,asc',
  });

  const categoriesQuery = useConfigCategories();

  // 뮤테이션
  const createMutation = useCreateConfig();
  const updateMutation = useUpdateConfig();
  const deleteMutation = useDeleteConfig();

  // 민감 건수 계산
  const sensitiveCount = useMemo(
    () => listQuery.data?.content.filter((r) => r.sensitive).length ?? 0,
    [listQuery.data]
  );

  const pageData = listQuery.data;
  const isListLoading = listQuery.isLoading;
  const isListError = listQuery.isError;
  const listError = listQuery.error as unknown as ApiError | undefined;
  const isForbidden = listError?.code === 'AUTH-403-001';
  const isEmpty = !isListLoading && !isListError && (pageData?.empty ?? false);
  const showTable = !isListLoading && !isListError && !isEmpty && !!pageData;

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  const handleSearch = useCallback(() => {
    setAppliedFilters({
      category: categoryInput,
      keyword: keywordInput,
      sensitiveOnly: sensitiveOnlyInput,
      page: 0,
    });
  }, [categoryInput, keywordInput, sensitiveOnlyInput]);

  const handleReset = useCallback(() => {
    setCategoryInput('');
    setKeywordInput('');
    setSensitiveOnlyInput(false);
    setAppliedFilters({ category: '', keyword: '', sensitiveOnly: false, page: 0 });
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleOpenCreate = useCallback(() => {
    setEditTarget(null);
    setFormOpen(true);
  }, []);

  const handleOpenEdit = useCallback((row: ConfigEntryResponse) => {
    setEditTarget(row);
    setFormOpen(true);
  }, []);

  const handleFormSave = useCallback(
    (values: ConfigEntryFormValues) => {
      const body = {
        configKey: values.configKey,
        configValue: values.configValue,
        valueType: values.valueType,
        sensitive: values.sensitive,
        description: values.description || undefined,
      };

      if (editTarget) {
        // 수정
        updateMutation.mutate(
          { key: editTarget.configKey, body },
          {
            onSuccess: () => {
              setFormOpen(false);
              setEditTarget(null);
              showToast(t('configCenter.toast.saved'));
            },
            onError: (err) => {
              const apiErr = err as unknown as ApiError;
              showToast(
                t(`configCenter.error.${apiErr.code}`, {
                  defaultValue: t('configCenter.error.network'),
                })
              );
            },
          }
        );
      } else {
        // 생성
        createMutation.mutate(body, {
          onSuccess: () => {
            setFormOpen(false);
            showToast(t('configCenter.toast.saved'));
          },
          onError: (err) => {
            const apiErr = err as unknown as ApiError;
            showToast(
              t(`configCenter.error.${apiErr.code}`, {
                defaultValue: t('configCenter.error.network'),
              })
            );
          },
        });
      }
    },
    [editTarget, createMutation, updateMutation, showToast, t]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.configKey, {
      onSuccess: () => {
        setDeleteTarget(null);
        showToast(t('configCenter.toast.deleted'));
      },
      onError: (err) => {
        setDeleteTarget(null);
        const apiErr = err as unknown as ApiError;
        showToast(
          t(`configCenter.error.${apiErr.code}`, {
            defaultValue: t('configCenter.error.network'),
          })
        );
      },
    });
  }, [deleteTarget, deleteMutation, showToast, t]);

  const isSaving = createMutation.isPending || updateMutation.isPending;

  const TABLE_HEADERS = useMemo(
    () => [
      t('configCenter.list.column.key'),
      t('configCenter.list.column.value'),
      t('configCenter.list.column.type'),
      t('configCenter.list.column.sensitive'),
      t('configCenter.list.column.updatedBy'),
      t('configCenter.list.column.actions'),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('configCenter.list.title')}
        breadcrumb={<span>{t('configCenter.list.title')}</span>}
        actions={
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
            {/* 변경 이력 버튼 */}
            <button
              type="button"
              onClick={() => setChangesOpen(true)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 500,
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('configCenter.list.button.changes')}
            </button>

            {/* + 설정 추가 (CONFIG_CENTER_WRITE 필요) */}
            <ButtonGuard requirePermission="CONFIG_CENTER_WRITE">
              {({ disabled, reason }) => (
                <button
                  type="button"
                  disabled={disabled}
                  title={reason}
                  onClick={handleOpenCreate}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    background: disabled
                      ? 'var(--color-surface-muted)'
                      : 'var(--color-brand-primary)',
                    color: disabled
                      ? 'var(--color-text-muted)'
                      : 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                    opacity: disabled ? 0.6 : 1,
                  }}
                >
                  + {t('configCenter.list.button.add')}
                </button>
              )}
            </ButtonGuard>
          </div>
        }
      />

      {/* 요약 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('configCenter.list.subtitle', {
            total: pageData.totalElements,
            sensitive: sensitiveCount,
          })}
        </p>
      )}

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('configCenter.list.title')}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--spacing-3)',
          padding: 'var(--spacing-3) var(--spacing-4)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          background: 'var(--color-surface-muted)',
          marginBottom: 'var(--spacing-4)',
          alignItems: 'flex-end',
        }}
      >
        {/* 카테고리 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('configCenter.list.filter.category')}
          <select
            value={categoryInput}
            onChange={(e) => setCategoryInput(e.target.value)}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              cursor: 'pointer',
              minWidth: 120,
            }}
          >
            <option value="">{t('configCenter.list.filter.categoryAll')}</option>
            {(categoriesQuery.data ?? []).map((cat) => (
              <option key={cat.category} value={cat.category}>
                {cat.labelKo} ({cat.count})
              </option>
            ))}
          </select>
        </label>

        {/* 키워드 검색 */}
        <label
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-1)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-secondary)',
          }}
        >
          {t('configCenter.list.filter.keyword')}
          <input
            type="text"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            placeholder={t('configCenter.list.filter.keywordPlaceholder')}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSearch();
            }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
              minWidth: 180,
            }}
          />
        </label>

        {/* 민감만 */}
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            cursor: 'pointer',
            paddingBottom: 'var(--spacing-1)',
          }}
        >
          <input
            type="checkbox"
            checked={sensitiveOnlyInput}
            onChange={(e) => setSensitiveOnlyInput(e.target.checked)}
          />
          {t('configCenter.list.filter.sensitiveOnly')}
        </label>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginLeft: 'auto' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isListLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isListLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              opacity: isListLoading ? 0.6 : 1,
            }}
          >
            {t('configCenter.list.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('configCenter.list.button.reset')}
          </button>
        </div>
      </div>

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* loading skeleton */}
        {isListLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('configCenter.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* error */}
        {isListError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            {isForbidden ? (
              <ErrorState
                title={t('configCenter.error.AUTH-403-001')}
                message={t('configCenter.error.AUTH-403-001.detail')}
                actions={[
                  {
                    label: t('configCenter.error.goHome'),
                    onClick: () => { window.location.href = '/'; },
                    variant: 'primary',
                  },
                ]}
              />
            ) : (
              <ErrorState
                title={t('configCenter.error.loadFailed')}
                message={listError?.message ?? t('configCenter.error.network')}
                onRetry={() => void listQuery.refetch()}
              />
            )}
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('configCenter.list.empty.title')}
              description={t('configCenter.list.empty.description')}
              action={
                <ButtonGuard requirePermission="CONFIG_CENTER_WRITE">
                  {({ disabled, reason }) => (
                    <button
                      type="button"
                      disabled={disabled}
                      title={reason}
                      onClick={handleOpenCreate}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: 'var(--spacing-2) var(--spacing-4)',
                        background: disabled
                          ? 'var(--color-surface-muted)'
                          : 'var(--color-brand-primary)',
                        color: disabled
                          ? 'var(--color-text-muted)'
                          : 'var(--color-text-inverse)',
                        border: 'none',
                        borderRadius: 'var(--radius-md)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 600,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        fontFamily: 'var(--font-family-sans)',
                        opacity: disabled ? 0.6 : 1,
                      }}
                    >
                      + {t('configCenter.list.button.add')}
                    </button>
                  )}
                </ButtonGuard>
              }
            />
          </div>
        )}

        {/* default — DataTable */}
        {showTable && pageData && (
          <>
            <div
              role="region"
              aria-label={t('configCenter.list.tableAriaLabel')}
              style={{ overflowX: 'auto' }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
                aria-label={t('configCenter.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => {
                    const isProtected = PROTECTED_KEYS.has(row.configKey);
                    return (
                      <tr
                        key={row.configKey}
                        aria-label={t('configCenter.list.rowAriaLabel', { key: row.configKey })}
                        style={{ transition: 'background 0.1s' }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background =
                            'var(--color-surface-muted)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLTableRowElement).style.background = '';
                        }}
                      >
                        {/* 키 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-sm)',
                            color: 'var(--color-text-primary)',
                            fontWeight: 500,
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-1)' }}>
                            {row.configKey}
                            {isProtected && (
                              <StatusPill tone="warning">
                                {t('configCenter.protected')}
                              </StatusPill>
                            )}
                          </div>
                        </td>

                        {/* 값 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            color: row.sensitive
                              ? 'var(--color-text-muted)'
                              : 'var(--color-text-secondary)',
                            fontFamily: 'var(--font-family-mono)',
                            fontSize: 'var(--font-size-sm)',
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.configValue}
                        </td>

                        {/* 타입 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            color: 'var(--color-text-secondary)',
                            fontSize: 'var(--font-size-xs)',
                          }}
                        >
                          <StatusPill tone="neutral">
                            {t(`configCenter.type.${row.valueType}`, { defaultValue: row.valueType })}
                          </StatusPill>
                        </td>

                        {/* 민감 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            textAlign: 'center',
                          }}
                        >
                          {row.sensitive ? (
                            <StatusPill tone="danger">
                              ● {t('configCenter.sensitive.yes')}
                            </StatusPill>
                          ) : (
                            <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>
                              {t('configCenter.sensitive.no')}
                            </span>
                          )}
                        </td>

                        {/* 수정자 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            color: 'var(--color-text-muted)',
                            fontSize: 'var(--font-size-xs)',
                          }}
                        >
                          {row.updatedBy ?? '-'}
                        </td>

                        {/* 작업 */}
                        <td
                          style={{
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--color-surface-border)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              gap: 'var(--spacing-1)',
                              alignItems: 'center',
                            }}
                          >
                            {/* 수정 버튼 */}
                            <ButtonGuard requirePermission="CONFIG_CENTER_WRITE">
                              {({ disabled, reason }) => (
                                <button
                                  type="button"
                                  disabled={disabled}
                                  title={reason ?? '수정'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenEdit(row);
                                  }}
                                  aria-label={`${row.configKey} 수정`}
                                  style={{
                                    padding: 'var(--spacing-1) var(--spacing-2)',
                                    background: 'transparent',
                                    border: '1px solid var(--color-surface-border)',
                                    borderRadius: 'var(--radius-sm)',
                                    fontSize: 'var(--font-size-xs)',
                                    color: disabled
                                      ? 'var(--color-text-muted)'
                                      : 'var(--color-text-secondary)',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    fontFamily: 'var(--font-family-sans)',
                                    opacity: disabled ? 0.5 : 1,
                                  }}
                                >
                                  ✎
                                </button>
                              )}
                            </ButtonGuard>

                            {/* 삭제 버튼 — 보호 키는 🔒 비활성 */}
                            <ButtonGuard requirePermission="CONFIG_CENTER_WRITE">
                              {({ disabled: permDisabled, reason }) => {
                                const isDisabled = permDisabled || isProtected;
                                const disabledTitle = isProtected
                                  ? t('configCenter.delete.confirm.protected')
                                  : reason;
                                return (
                                  <button
                                    type="button"
                                    disabled={isDisabled}
                                    title={disabledTitle ?? '삭제'}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!isDisabled) setDeleteTarget(row);
                                    }}
                                    aria-label={`${row.configKey} 삭제`}
                                    aria-disabled={isDisabled}
                                    style={{
                                      padding: 'var(--spacing-1) var(--spacing-2)',
                                      background: 'transparent',
                                      border: `1px solid ${isDisabled ? 'var(--color-surface-border)' : 'var(--color-danger)'}`,
                                      borderRadius: 'var(--radius-sm)',
                                      fontSize: 'var(--font-size-xs)',
                                      color: isDisabled
                                        ? 'var(--color-text-muted)'
                                        : 'var(--color-danger)',
                                      cursor: isDisabled ? 'not-allowed' : 'pointer',
                                      fontFamily: 'var(--font-family-sans)',
                                      opacity: isDisabled ? 0.5 : 1,
                                    }}
                                  >
                                    {isProtected ? '🔒' : '🗑'}
                                  </button>
                                );
                              }}
                            </ButtonGuard>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                }}
              >
                {t('configCenter.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min(
                    (pageData.number + 1) * pageData.size,
                    pageData.totalElements
                  ),
                })}
              </span>
              <nav
                aria-label={t('configCenter.list.pagination.nav')}
                style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}
              >
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(appliedFilters.page - 1)}
                  aria-label={t('configCenter.list.pagination.prev')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('configCenter.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28,
                      height: 28,
                      padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background:
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-elevated)',
                      border: `1px solid ${
                        i === pageData.number
                          ? 'var(--color-brand-primary)'
                          : 'var(--color-surface-border)'
                      }`,
                      color:
                        i === pageData.number
                          ? 'var(--color-text-inverse)'
                          : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(appliedFilters.page + 1)}
                  aria-label={t('configCenter.list.pagination.next')}
                  style={{
                    minWidth: 28,
                    height: 28,
                    padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* 공통 모듈 패널 */}
      <ConfigCommonUsagePanel />

      {/* 설정 생성/수정 FormDialog (dialog 상태) */}
      {formOpen && (
        <ConfigEntryFormDialog
          editTarget={editTarget}
          isSaving={isSaving}
          onSave={handleFormSave}
          onClose={() => {
            if (!isSaving) {
              setFormOpen(false);
              setEditTarget(null);
            }
          }}
        />
      )}

      {/* 변경 이력 Drawer (dialog 상태) */}
      {changesOpen && (
        <ConfigChangeFeedDrawer onClose={() => setChangesOpen(false)} />
      )}

      {/* 삭제 confirm dialog (dialog 상태) */}
      {deleteTarget && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-config-dialog-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape' && !deleteMutation.isPending)
              setDeleteTarget(null);
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 440,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="delete-config-dialog-title"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                marginBottom: 'var(--spacing-3)',
                color: 'var(--color-text-primary)',
              }}
            >
              {t('configCenter.delete.confirm.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('configCenter.delete.confirm.description')}
            </p>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                fontFamily: 'var(--font-family-mono)',
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--spacing-5)',
                padding: 'var(--spacing-2) var(--spacing-3)',
                background: 'var(--color-surface-muted)',
                borderRadius: 'var(--radius-md)',
              }}
            >
              {deleteTarget.configKey}
            </p>
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                justifyContent: 'flex-end',
              }}
            >
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleteMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {t('configCenter.delete.confirm.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={deleteMutation.isPending}
                autoFocus
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-danger)',
                  color: 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: deleteMutation.isPending ? 0.6 : 1,
                  fontFamily: 'var(--font-family-sans)',
                }}
              >
                {deleteMutation.isPending
                  ? t('configCenter.delete.deleting')
                  : t('configCenter.delete.confirm.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 360,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 + skeleton animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 560px; }
        }
      `}</style>
    </div>
  );
}
